<template>
  <div>
    <div class="image-card" v-if="item">
      <div
        class="image-card__img-container"
        :class="{
          'rounded-all': !showInfoBlock && !hasFooterSlot,
          'rounded-bottom': shouldRoundImageBottom,
          'transparent-media': isAlphaMedia,
        }"
        ref="containerRef"
        @click="$emit('select', item)"
        @mouseenter="onHoverStart"
        @mouseleave="onHoverEnd"
        @pointerenter="onPointerEnter"
        @pointerleave="onPointerLeave"
        @mouseover="onMouseOver"
        @mouseout="onMouseOut"
        @touchstart.passive="onTouchStart"
        @touchmove.passive="onTouchMove"
        @touchend.passive="onTouchEnd"
        @touchcancel.passive="onTouchEnd"
      >
        <!-- Saving overlay -->
        <div v-if="saving" class="saving-overlay" @click.stop>
          <div class="saving-shimmer"></div>
          <small class="saving-text">Saving…</small>
        </div>
        <!-- Regular image display -->
        <!-- Document display -->
        <div
          v-if="isDocumentItem"
          class="document-container"
          :class="{
            'rounded-all': !showInfoBlock && !hasFooterSlot,
            'rounded-bottom': shouldRoundImageBottom,
          }"
        >
          <div class="document-frame" :class="{ pdf: isPdfDocument }">
            <template v-if="isPdfDocument">
              <embed
                :src="primaryDocumentUrl"
                type="application/pdf"
                class="document-embed"
              />
            </template>
            <template v-else-if="isTextDocument && documentTextContent">
              <div class="document-text-preview">
                <div class="document-text-header">
                  <span class="document-type-badge">{{ documentExtension }}</span>
                  <span class="document-text-name">{{ documentDisplayName }}</span>
                </div>
                <pre class="document-text-body">{{ documentTextContent }}</pre>
              </div>
            </template>
            <template v-else>
              <div class="document-fallback-card">
                <div class="document-icon">
                  <svg class="doc-svg" aria-hidden="true">
                    <use href="@/assets/icons/iconset.svg#file"></use>
                  </svg>
                </div>
                <span v-if="documentExtension" class="document-type-badge">{{ documentExtension }}</span>
                <div class="document-name" v-if="documentDisplayName">
                  {{ documentDisplayName }}
                </div>
              </div>
            </template>
          </div>
        </div>
        <!-- Audio display -->
        <div
          v-else-if="isAudioItem"
          class="audio-container"
          :class="{
            'rounded-all': !showInfoBlock && !hasFooterSlot,
            'rounded-bottom': shouldRoundImageBottom,
            'has-cover': audioCoverImage,
          }"
        >
          <!-- Cover image if available - clickable to open slideshow -->
          <img
            v-if="audioCoverImage"
            :src="audioCoverThumbnailUrl"
            :srcset="audioCoverSrcset"
            :sizes="audioCoverSrcset ? '(max-width: 590px) 50vw, 33vw' : undefined"
            alt="Audio cover"
            class="audio-cover-image audio-cover-clickable"
            loading="lazy"
            decoding="async"
            @click="$emit('select', item)"
          />

          <!-- Expand button to open slideshow (only shown when there's a cover) -->
          <button
            v-if="audioCoverImage"
            class="audio-expand-button"
            @click.stop="$emit('select', item)"
            aria-label="View details"
          >
            <svg
              class="audio-expand-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <use href="@/assets/icons/iconset.svg#externalLink-compact"></use>
            </svg>
          </button>

          <!-- Native Audio Element - always plays inline -->
          <div
            class="audio-native-wrapper"
            :class="{ 'with-cover': audioCoverImage }"
            @click.stop
          >
            <audio
              ref="audioElement"
              :src="item.mediaUrl || item.audioUrl"
              controls
              preload="metadata"
              class="native-audio-player"
              @ended="onAudioEnded"
              @play="onAudioPlay"
              @pause="onAudioPause"
            ></audio>
          </div>

          <!-- Expand footer for audio without cover (click to open overlay) -->
          <div v-if="!audioCoverImage" class="audio-player-bg"></div>
          <div
            v-if="!audioCoverImage"
            class="audio-expand-footer rounded-all-bottom"
            @click.stop="$emit('select', item)"
          >
            <span class="audio-expand-label">Expand</span>
          </div>
        </div>
        <!-- Regular image display -->
        <!-- Multi-image grid for posts with multiple images -->
        <div
          v-if="isMultiImagePost"
          class="multi-image-grid"
          :class="[
            `grid-${Math.min(multiImageCount, 4)}`,
            {
              'rounded-all': !showInfoBlock && !hasFooterSlot,
              'rounded-bottom': shouldRoundImageBottom,
            },
          ]"
        >
          <div
            v-for="(media, idx) in gridMediaItems"
            :key="idx"
            class="grid-cell"
            :class="[`cell-${idx}`, { 'grid-cell-video': media.type === 'video' }]"
          >
            <img
              v-if="media.poster || media.type !== 'video'"
              :src="gridImageSrc(media, idx)"
              :srcset="media.srcset || undefined"
              :sizes="
                media.srcset ? '(max-width: 590px) 25vw, 16.5vw' : undefined
              "
              loading="lazy"
              decoding="async"
              :alt="media.type === 'video' ? `Video ${idx + 1}` : `Image ${idx + 1}`"
              class="grid-img"
              @error="handleGridImageError(media, idx)"
            />
            <video
              v-else
              :src="media.url"
              muted
              playsinline
              preload="metadata"
              class="grid-img"
            ></video>
            <div
              v-if="media.type === 'video'"
              class="grid-video-overlay"
              aria-hidden="true"
            >
              <svg class="grid-play-icon" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" fill="currentColor" />
              </svg>
            </div>
            <!-- +N overlay on last cell when more than 4 images -->
            <div
              v-if="idx === 3 && multiImageCount > 4"
              class="grid-more-overlay"
            >
              <svg class="grid-more-icon" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#grid"></use>
              </svg>
              <span class="grid-more-count">+{{ multiImageCount - 4 }}</span>
            </div>
          </div>
        </div>
        <!-- Single image display -->
        <img
          v-else-if="hasImageMedia && !isVideoItem"
          :key="cardImageKey"
          :src="cardImageSrc"
          :srcset="computedSrcset"
          :sizes="computedSizes"
          loading="lazy"
          decoding="async"
          :crossorigin="cardImageCrossorigin"
          alt="Image card"
          class="image-card__img"
          :class="{
            'rounded-all':
              ((item.type === 'product' || item.type === 'service') &&
                !showInfoBlock) ||
              (!showInfoBlock && !hasFooterSlot),
            'rounded-bottom': shouldRoundImageBottom,
            'transparent-media': isAlphaMedia,
          }"
          @load="handleImageLoaded"
          @error="handleImageError"
        />
        <!-- Video poster thumbnail – always visible, provides layout sizing -->
        <img
          v-if="isVideoItem && posterUrl"
          :src="posterThumbnailUrl"
          :srcset="posterSrcset"
          :sizes="posterSrcset ? '(max-width: 590px) 50vw, 33vw' : undefined"
          loading="lazy"
          decoding="async"
          alt="Video thumbnail"
          class="image-card__img video-poster-thumb"
          :class="{
            'rounded-all':
              ((item.type === 'product' || item.type === 'service') &&
                !showInfoBlock) ||
              (!showInfoBlock && !hasFooterSlot),
            'rounded-bottom': shouldRoundImageBottom,
          }"
        />
        <!-- Play overlay for video posts (hide while preview playing) -->
        <div v-if="isVideoItem" v-show="!isPreviewPlaying" class="play-overlay">
          <svg class="play-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5v14l11-7z" fill="currentColor" />
          </svg>
        </div>
        <!-- Video element for preview (render even if there's a poster image) -->
        <video
          v-if="isVideoItem"
          ref="videoRef"
          class="image-card__video"
          :class="{
            'no-poster': !posterUrl,
            'is-playing': isPreviewPlaying,
            'rounded-all':
              (!showInfoBlock && !hasFooterSlot) ||
              ((item.type === 'product' || item.type === 'service') &&
                !showInfoBlock),
            'rounded-bottom': shouldRoundImageBottom,
          }"
          :src="videoLoadRequested || !posterUrl ? videoSrc || undefined : undefined"
          :poster="posterThumbnailUrl"
          muted
          playsinline
          :preload="videoLoadRequested || !posterUrl ? 'metadata' : 'none'"
          tabindex="-1"
        ></video>
        <!-- YouTube embed — thumbnail + play overlay (click opens overlay) -->
        <div
          v-else-if="hasEmbed && isYouTubeEmbed"
          class="embed-container"
          :class="{
            'rounded-all': !showInfoBlock && !hasFooterSlot,
            'rounded-bottom': shouldRoundImageBottom,
            'embed-vertical': isVerticalYouTubeEmbed,
            'embed-square': isSquareYouTubeEmbed,
          }"
        >
          <img
            v-if="embedThumbnailUrl"
            class="embed-thumbnail"
            :src="embedThumbnailUrl"
            alt="Video thumbnail"
            loading="lazy"
          />
          <div v-else class="embed-placeholder">
            <svg class="small"><use href="@/assets/icons/iconset.svg#play" /></svg>
          </div>
          <div class="embed-play-overlay">
            <div class="embed-play-btn">
              <svg class="play-icon-svg"><use href="@/assets/icons/iconset.svg#play_filled" /></svg>
            </div>
          </div>
        </div>
        <!-- Spotify embed — render live widget inline -->
        <div
          v-else-if="hasEmbed && isSpotifyEmbed"
          class="embed-widget-wrapper spotify-embed-wrapper"
        >
          <div
            class="embed-widget-container spotify-embed-container"
            v-html="sanitizedEmbed"
            @click.stop
          ></div>
          <!-- Expand footer for Spotify embeds (widget swallows clicks) -->
          <div
            class="embed-expand-footer"
            :class="{
              'rounded-all-bottom': !showInfoBlock && !hasFooterSlot,
            }"
            @click.stop="$emit('select', item)"
          >
            <span class="embed-expand-label">Expand</span>
          </div>
        </div>
        <!-- SoundCloud / other embed — render live widget inline -->
        <div
          v-else-if="hasEmbed"
          class="embed-widget-wrapper"
        >
          <div
            class="embed-widget-container"
            :class="{
              'rounded-all': !showInfoBlock && !hasFooterSlot && !isSoundCloudEmbed,
              'rounded-bottom': shouldRoundImageBottom && !isSoundCloudEmbed,
            }"
            v-html="sanitizedEmbed"
            @click.stop
          ></div>
          <!-- Expand footer for SoundCloud embeds (widget swallows clicks) -->
          <div
            v-if="isSoundCloudEmbed"
            class="embed-expand-footer"
            :class="{
              'rounded-all-bottom': !showInfoBlock && !hasFooterSlot,
            }"
            @click.stop="$emit('select', item)"
          >
            <span class="embed-expand-label">Expand</span>
          </div>
        </div>
        <!-- NFT colorful placeholder (updated icon) -->
        <div
          v-else-if="
            item.type === 'nft' && item.hasPlaceholder && item.placeholderData
          "
          class="nft-placeholder-container"
          :class="[
            {
              'rounded-all': !showInfoBlock && !hasFooterSlot,
              'rounded-bottom': shouldRoundImageBottom,
            },
            item.placeholderData.placeholderClass,
          ]"
        >
          <div class="nft-placeholder-icon" aria-label="NFT placeholder">
            <svg class="placeholder-svg" role="img" aria-hidden="false">
              <use href="@/assets/icons/iconset.svg#nft" />
            </svg>
          </div>
          <div class="nft-placeholder-text">
            {{ item.placeholderData.placeholderText }}
          </div>
        </div>
        <!-- Generic visual placeholder -->
        <div
          v-else-if="showVisualPlaceholder"
          class="empty-placeholder"
          ref="placeholderRef"
          :style="placeholderStyle"
          :class="{
            'rounded-all':
              ((item.type === 'product' || item.type === 'service') &&
                !showInfoBlock) ||
              (!showInfoBlock && !hasFooterSlot),
            'rounded-bottom': shouldRoundImageBottom,
          }"
          role="img"
          :aria-label="[displayTitle, 'no image'].filter(Boolean).join(' – ')"
        >
          <div class="empty-placeholder-content">
            <div class="empty-placeholder-title" v-if="displayTitle">
              {{ displayTitle }}
            </div>
            <div class="empty-placeholder-caption" v-if="placeholderText">
              <small>{{ placeholderText }}</small>
            </div>
          </div>
        </div>
      </div>
      <!-- Clap button only - always visible in corner -->
      <slot name="clap-button"></slot>
    </div>
    <!-- Unified info block with optional footer -->
    <div
      class="image-card__info"
      :class="{ 'no-caption': !showInfoBlock }"
      v-if="showInfoBlock || hasFooterSlot || hasDropdownSlot"
    >
      <!-- Caption and dropdown on same row -->
      <div v-if="showInfoBlock || hasDropdownSlot" class="caption-row">
        <small v-if="showInfoBlock" class="nomargintopbottom caption-text">
          <span
            :class="{ 'audio-text-clickable': isAudioItem && !audioCoverImage }"
            @click.stop="
              isAudioItem && !audioCoverImage ? $emit('select', item) : null
            "
            ><LinkifiedText :text="cardTextTruncated" /></span
          ><span
            v-if="isTextTruncated"
            class="read-more-link"
            @click.stop="$emit('select', item)"
            >Read more</span
          >
        </small>
        <div class="row tintGap alignCentreRow">
          <!-- Batch mint selection checkbox for multi-image posts -->
          <label v-if="selectable" class="batch-select-checkbox" @click.stop>
            <input
              type="checkbox"
              :checked="selected"
              @change="
                $emit('selection-change', {
                  item,
                  selected: $event.target.checked,
                })
              "
            />
            <span class="checkbox-label">Mint</span>
          </label>
          <span
            v-if="
              item.nftTokenId ||
              item.nftContractAddress ||
              item.contractAddress ||
              item.mintStatus === 'minted'
            "
            class="minted-label"
            >NFT</span
          >
          <div
            v-if="hasDropdownSlot"
            class="dropdown-menu-container"
            @click.stop
          >
            <slot name="dropdown-menu"></slot>
          </div>
        </div>
      </div>
      <!-- Footer slot below caption row -->
      <div v-if="hasFooterSlot" class="image-card__info-footer">
        <slot name="info-footer"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { db, doc, getDoc } from "@/services/legacyBrowserPersistenceBoundary";
import { resolveCardText, truncateText } from "@/utils/resolveCardText";
import { sanitizeEmbedHtml, isVerticalEmbed, isSquareEmbed } from "@/utils/postComposerUtils";
import { extractCID } from "@/utils/ipfsUtils";
import { assetIdForAuthorityUrl } from "@/services/authorityAssetUrlRegistry";
import { itemMediaUrlResolver } from "@/services/itemMediaUrlResolver";
import { Comment, Text, Fragment } from "vue";
import LinkifiedText from "@/components/GenericComponents/LinkifiedText.vue";
// Ordered IPFS gateways tried when a thumbnail fails (e.g. ipfs.io 403/rate-limit).
const IPFS_FALLBACK_GATEWAYS = [
  "https://gateway.pinata.cloud/ipfs/",
  "https://dweb.link/ipfs/",
  "https://nftstorage.link/ipfs/",
  "https://ipfs.io/ipfs/",
];
export default {
  name: "ImageCard",
  components: { LinkifiedText },
  props: {
    item: {
      type: Object,
      required: true,
    },
    favouritesLoading: {
      type: Object,
      default: () => ({}),
    },
    saving: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // Single source-of-truth for compact card text
    cardText() {
      return resolveCardText(this.item);
    },
    cardTextTruncated() {
      // Dynamic again, but never shorter than 85
      const limit = Math.max(85, this.captionMaxChars || 120);
      return truncateText(this.cardText, limit);
    },
    isTextTruncated() {
      // Check if the text was actually truncated
      return this.cardText.length > this.cardTextTruncated.length;
    },
    showInfoBlock() {
      // Only show when there is media (no visual placeholder) and there is text
      return !!this.cardText && !this.showVisualPlaceholder;
    },
    shouldRoundImageBottom() {
      return !this.showInfoBlock;
    },
    placeholderText() {
      // For visual placeholder we still want to show any available free text
      // Prefer description then caption then itemDescription without truncation (placeholder scrolls)
      const desc = (this.item?.description || "").toString().trim();
      const cap = (this.item?.caption || "").toString().trim();
      const idesc = (this.item?.itemDescription || "").toString().trim();
      return desc || cap || idesc || "";
    },
    isTextOnlyMedia() {
      // Embed posts (YouTube, SoundCloud) are NOT text-only — they render an iframe
      if (this.hasEmbed) return false;
      return (
        this.item?.type === "media" &&
        ["poster", "tweet", "post", "text"].includes(this.item?.mediaType) &&
        !this.hasImageMedia
      );
    },
    hasEmbed() {
      return !!this.item?.embedHtml;
    },
    isYouTubeEmbed() {
      if (this.item?.embedProvider === "youtube") return true;
      // Fallback: check iframe src in embedHtml
      return /youtube|youtu\.be/i.test(this.item?.embedHtml || "");
    },
    isSoundCloudEmbed() {
      if (this.item?.embedProvider === "soundcloud") return true;
      return /soundcloud\.com/i.test(this.item?.embedHtml || "");
    },
    isSpotifyEmbed() {
      if (this.item?.embedProvider === "spotify") return true;
      return /open\.spotify\.com/i.test(this.item?.embedUrl || this.item?.embedHtml || "");
    },
    isVerticalYouTubeEmbed() {
      return this.isYouTubeEmbed && isVerticalEmbed(this.item) && !isSquareEmbed(this.item);
    },
    isSquareYouTubeEmbed() {
      return this.isYouTubeEmbed && isSquareEmbed(this.item);
    },
    embedThumbnailUrl() {
      // Prefer stored thumbnailUrl
      if (this.item?.thumbnailUrl) return this.item.thumbnailUrl;
      // Fallback: derive from videoId in meta or parse from embedHtml
      const videoId = this.item?.meta?.videoId || this.extractVideoIdFromEmbed();
      if (videoId) return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      return ""; // SoundCloud or unknown — no thumbnail
    },
    sanitizedEmbed() {
      if (!this.item?.embedHtml) return "";
      return sanitizeEmbedHtml(this.item.embedHtml);
    },
    embedExternalUrl() {
      const raw = (this.item?.embedUrl || "").trim();
      if (/^https?:\/\//i.test(raw)) return raw;
      if (/^(?:open\.)?spotify\.com\//i.test(raw)) return `https://${raw}`;
      const match = (this.item?.embedHtml || "").match(/open\.spotify\.com\/embed\/(?:intl-[^/]+\/)?(track|album|playlist|artist|show|episode)\/([\w-]+)/i);
      if (!match) return "";
      return `https://open.spotify.com/${match[1]}/${match[2]}`;
    },
    hasImageMedia() {
      // For document/audio items, never treat as image media
      if (this.isDocumentItem) return false;
      if (this.isAudioItem) return false;
      // A permanently failed image falls back to the placeholder, not a broken <img>
      if (this.imageLoadFailed) return false;

      // true if there is an image url (not a video file)
      const url = this.getDisplayImage();
      if (!url) return false;
      // crude check: if url ends with a common video extension treat as not image
      if (/(\.(mp4|webm|mov|m4v))(\?|$)/i.test(url)) return false;
      return true;
    },
    // Alpha-capable media: detected from URL heuristic OR canvas sampling after load
    isAlphaMedia() {
      if (this.detectedTransparent) return true;
      const url = this.getDisplayImage();
      if (!url) return false;
      // URL extension check (handles Firebase Storage %2F paths + clean paths)
      if (/\.(png|gif|svg)(\?|%3F|$)/i.test(url)) return true;
      // data URI mime type check
      if (/^data:image\/(png|gif|svg\+xml|webp|avif)[;,]/i.test(url)) return true;
      return false;
    },
    // The raw URL we will attempt to display (image or video file)
    displayUrl() {
      return this.getDisplayImage();
    },
    // Heuristic to determine if the displayUrl represents a video
    displayIsVideo() {
      const url = this.displayUrl || "";
      if (this.item?.mediaType === "video") return true;
      const imgs = Array.isArray(this.item?.images) ? this.item.images : [];
      // Mixed-media posts can contain videos, but they should still render as a grid.
      if (imgs.length > 1) return false;
      const types = Array.isArray(this.item?.mediaTypes)
        ? this.item.mediaTypes
        : [];
      if (types[0] === "video") return true;
      return /(\.(mp4|webm|mov|m4v))(\?|$)/i.test(url);
    },
    isVideoItem() {
      return this.item?.mediaType === "video" || this.displayIsVideo;
    },
    isDocumentItem() {
      if (this.item?.mediaType === "documents") return true;
      // Detect document-type URLs for NFTs and other items without mediaType
      const url = this.getDisplayImage() || "";
      if (url && /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|md|csv|json|rtf|odt)(\?|$)/i.test(url)) return true;
      // Check Firebase Storage path for documents folder
      if (url && /\/documents%2F/i.test(url)) return true;
      // A failed image implies a hidden document only for non-NFT items
      // (e.g. IPFS-pinned .txt/.pdf without an extension). NFTs and authority
      // items (image-only media) fall back to a placeholder.
      if (
        this.imageLoadFailed &&
        this.item?.type !== "nft" &&
        this.item?._authorityPresentation !== true
      ) {
        return true;
      }
      return false;
    },
    isAudioItem() {
      return this.item?.mediaType === "audio";
    },
    // Multi-image post detection
    isMultiImagePost() {
      // Mixed media posts use images[] as the ordered visual media list.
      if (this.isDocumentItem || this.isAudioItem) return false;
      if (this.item?.mediaType === "video") return false;
      const imgs = Array.isArray(this.item?.images) ? this.item.images : [];
      return imgs.filter(Boolean).length > 1;
    },
    multiImageCount() {
      if (!this.isMultiImagePost) return 0;
      const imgs = Array.isArray(this.item?.images) ? this.item.images : [];
      return imgs.filter(Boolean).length;
    },
    gridMediaItems() {
      // Return first 4 visual media items for the grid display.
      if (!this.isMultiImagePost) return [];
      const imgs = Array.isArray(this.item?.images) ? this.item.images : [];
      const types = Array.isArray(this.item?.mediaTypes)
        ? this.item.mediaTypes
        : [];
      const thumbs = Array.isArray(this.item?.mediaThumbnails)
        ? this.item.mediaThumbnails
        : [];
      const perImageVariants = Array.isArray(this.item?.mediaVariants)
        ? this.item.mediaVariants
        : [];
      const posterVariants = Array.isArray(this.item?.mediaThumbnailVariants)
        ? this.item.mediaThumbnailVariants
        : [];
      return imgs
        .map((url, idx) => {
          const declaredType = String(types[idx] || "").toLowerCase();
          const isVideo =
            declaredType === "video" ||
            /(\.(mp4|webm|mov|m4v))(\?|$)/i.test(url || "");
          const variants = isVideo
            ? posterVariants[idx]
            : perImageVariants[idx] ||
              (idx === (this.item?.thumbnailIndex ?? 0)
                ? this.item?.variants
                : null);
          const poster = isVideo ? thumbs[idx] || "" : "";
          return {
            url,
            type: isVideo ? "video" : "image",
            poster: isVideo
              ? this.pickCardVariant(variants, poster)
              : "",
            thumbnailUrl: isVideo
              ? ""
              : this.pickCardVariant(variants, url),
            srcset: this.buildVariantSrcset(variants, 1024),
          };
        })
        .filter((media) => media.url)
        .slice(0, 4);
    },
    audioCoverImage() {
      // Get cover image for audio post (first image if available)
      if (!this.isAudioItem) return null;
      const imgs = Array.isArray(this.item?.images) ? this.item.images : [];
      return imgs[0] || null;
    },
    audioCoverVariants() {
      const perImageVariants = Array.isArray(this.item?.mediaVariants)
        ? this.item.mediaVariants[0]
        : null;
      return perImageVariants || this.item?.variants || this.item?.imageVariants;
    },
    audioCoverThumbnailUrl() {
      return this.pickCardVariant(this.audioCoverVariants, this.audioCoverImage);
    },
    audioCoverSrcset() {
      return this.buildVariantSrcset(this.audioCoverVariants, 1024) || undefined;
    },
    documentDisplayName() {
      // Try to get document name from metadata
      if (
        this.item?.documentMetadata &&
        Array.isArray(this.item.documentMetadata)
      ) {
        const firstDoc = this.item.documentMetadata[0];
        if (firstDoc?.name) return firstDoc.name;
      }
      // Fallback to item name or title
      return this.item?.itemName || this.item?.title || "Document";
    },
    primaryDocumentUrl() {
      // Get the first document URL from documentUrls field (new format)
      if (this.item?.documentUrls && Array.isArray(this.item.documentUrls)) {
        return this.item.documentUrls[0] || "";
      }
      // Fallback to images array for backward compatibility
      const imgs = Array.isArray(this.item?.images) ? this.item.images : [];
      if (imgs[0]) return imgs[0];
      // Fallback to mediaUrl / image (covers NFT items)
      return this.item?.mediaUrl || this.item?.image || "";
    },
    isPdfDocument() {
      // Check if the primary document is a PDF
      const url = this.primaryDocumentUrl;
      if (!url) return false;

      // Check metadata first for accurate type detection
      if (
        this.item?.documentMetadata &&
        Array.isArray(this.item.documentMetadata)
      ) {
        const firstDoc = this.item.documentMetadata[0];
        if (firstDoc?.type === "application/pdf") return true;
      }

      // Fallback to URL extension check
      return url.endsWith(".pdf") || url.includes(".pdf?");
    },
    isTextDocument() {
      // Check if primary document is a text-readable file
      if (!this.isDocumentItem) return false;
      const meta = this.item?.documentMetadata;
      if (Array.isArray(meta) && meta.length) {
        const firstDoc = meta[0];
        const type = (firstDoc?.type || "").toLowerCase();
        const name = (firstDoc?.name || "").toLowerCase();
        if (
          type === "text/plain" || type === "text/markdown" ||
          type === "text/csv" || type === "application/json" ||
          name.endsWith(".txt") || name.endsWith(".md") ||
          name.endsWith(".csv") || name.endsWith(".json")
        ) return true;
      }
      // Check URL extension
      const url = this.primaryDocumentUrl;
      if (url && (/\.(txt|md|csv|json)(\?|$)/i.test(url))) return true;
      // Check documentTextContents from preview (PostComposer passes this)
      if (this.item?.documentTextContents && Object.keys(this.item.documentTextContents).length) return true;
      // Check documents array (local preview with File objects)
      const docs = this.item?.documents;
      if (Array.isArray(docs) && docs.length) {
        const d = docs[0];
        const name = (d?.name || "").toLowerCase();
        const type = (d?.type || "").toLowerCase();
        if (
          type === "text/plain" || type === "text/markdown" ||
          name.endsWith(".txt") || name.endsWith(".md") ||
          name.endsWith(".csv") || name.endsWith(".json")
        ) return true;
      }
      return false;
    },
    documentTextContent() {
      // Get text content for preview (passed from PostComposer via item.documentTextContents)
      if (this.item?.documentTextContents) {
        const keys = Object.keys(this.item.documentTextContents);
        if (keys.length) return this.item.documentTextContents[keys[0]] || "";
      }
      return "";
    },
    documentExtension() {
      // Get the file extension for display badge
      const name = this.documentDisplayName || "";
      const nameParts = name.split(".");
      if (nameParts.length > 1) return nameParts.pop().toUpperCase();
      // Fallback: extract extension from the document URL
      const url = this.primaryDocumentUrl || "";
      if (url) {
        const match = url.match(/\.([a-zA-Z0-9]{2,5})(\?|$)/);
        if (match) return match[1].toUpperCase();
      }
      return "";
    },
    posterUrl() {
      const imgs = Array.isArray(this.item?.images) ? this.item.images : [];
      // If this is a video post, prefer the configured thumbnail index
      if (this.item?.mediaType === "video" && imgs.length) {
        const tIdx = this.item?.thumbnailIndex ?? 0;
        const candidate = imgs[tIdx] ?? imgs[0];
        // Avoid accidentally returning the mediaUrl if present in images
        if (
          candidate &&
          candidate !== this.item?.mediaUrl &&
          !/(\.(mp4|webm|mov|m4v))(\?|$)/i.test(candidate)
        ) {
          return candidate;
        }
      }
      // For video posts: fall back to auto-generated poster from mediaThumbnails
      if (this.item?.mediaType === "video") {
        const thumbs = Array.isArray(this.item.mediaThumbnails)
          ? this.item.mediaThumbnails
          : [];
        const poster = thumbs.find((u) => u && typeof u === "string");
        if (poster) return poster;
      }
      // Otherwise pick the first URL that doesn't look like a video file
      const imageLike = imgs.find(
        (u) => u && !/(\.(mp4|webm|mov|m4v))(\?|$)/i.test(u),
      );
      if (imageLike) return imageLike;
      const v = this.item?.variants || this.item?.imageVariants;
      if (v && typeof v === "object") {
        return v.md || v.lg || v.sm || v.xl || Object.values(v)[0];
      }
      // fallback to displayUrl only if it doesn't look like a video
      const url = this.displayUrl || "";
      return /(\.(mp4|webm|mov|m4v))(\?|$)/i.test(url) ? undefined : url;
    },
    posterVariants() {
      const imgs = Array.isArray(this.item?.images) ? this.item.images : [];
      const perImageVariants = Array.isArray(this.item?.mediaVariants)
        ? this.item.mediaVariants
        : [];
      const thumbnailIndex = this.item?.thumbnailIndex ?? 0;
      const customPoster = imgs[thumbnailIndex] ?? imgs[0];
      if (
        customPoster &&
        customPoster !== this.item?.mediaUrl &&
        !/(\.(mp4|webm|mov|m4v))(\?|$)/i.test(customPoster)
      ) {
        return (
          perImageVariants[thumbnailIndex] ||
          perImageVariants[0] ||
          this.item?.variants ||
          this.item?.imageVariants
        );
      }

      const thumbnails = Array.isArray(this.item?.mediaThumbnails)
        ? this.item.mediaThumbnails
        : [];
      const thumbnailVariants = Array.isArray(this.item?.mediaThumbnailVariants)
        ? this.item.mediaThumbnailVariants
        : [];
      const posterIndex = thumbnails.findIndex((url) => url === this.posterUrl);
      return posterIndex >= 0 ? thumbnailVariants[posterIndex] : null;
    },
    posterThumbnailUrl() {
      return this.pickCardVariant(this.posterVariants, this.posterUrl);
    },
    posterSrcset() {
      return this.buildVariantSrcset(this.posterVariants, 1024) || undefined;
    },
    videoSrc() {
      // If we previously resolved a missing video source, prefer it
      if (this.resolvedVideoSrc) return this.resolvedVideoSrc;
      // Build a robust set of candidates based on docs + current save flow.
      const isVideoish = (u) => /(\.(mp4|webm|mov|m4v))(\?|$)/i.test(u || "");
      const mediaType = this.item?.mediaType;
      const fromMedia = this.item?.mediaUrl || "";
      const altMedia =
        this.item?.videoUrl ||
        this.item?.processedVideoUrl ||
        this.item?.originalVideoUrl ||
        "";
      if (mediaType === "video" && fromMedia) return fromMedia;

      const imgs = Array.isArray(this.item?.images) ? this.item.images : [];
      const tIdx = this.item?.thumbnailIndex ?? 0;
      const poster = imgs[tIdx] ?? imgs[0] ?? "";
      // If declared video and there's a non-poster entry, prefer it even if
      // the URL lacks a video extension (common for Firebase download URLs).
      if (mediaType === "video") {
        const nonPosterAny = imgs.find(
          (u, i) => i !== tIdx && u && u !== poster,
        );
        if (nonPosterAny) return nonPosterAny;
      }
      // Any image entries except the poster that look like video
      const nonPosterVideo = imgs.find((u, i) => i !== tIdx && isVideoish(u));
      if (nonPosterVideo) return nonPosterVideo;

      // If no explicit video url yet, consider alternate fields
      if (altMedia) return altMedia;

      // If images only has one entry and it's a video, use it (legacy records)
      if (imgs.length === 1 && isVideoish(imgs[0])) return imgs[0];

      // Final fallback for declared video items with extension-less URLs or single-entry arrays:
      // try the poster itself as the video src and let play() decide.
      if (mediaType === "video" && poster) return poster;

      // Never fall back to an image poster as video src; return empty string
      // so the preview logic can keep the overlay and skip play.
      return "";
    },
    showVisualPlaceholder() {
      // Show placeholder if no image/video/embed is available. For text-only media, we DO want the
      // placeholder so it can render the title/caption nicely. Never show for documents, audio, or embeds.
      return (
        !this.isDocumentItem &&
        !this.isAudioItem &&
        !this.hasImageMedia &&
        !this.displayIsVideo &&
        !this.hasEmbed
      );
    },
    // removed truncatedCaption in favor of cardTextTruncated
    hasFooterSlot() {
      const s = this.$slots["info-footer"];
      if (!s) return false;
      try {
        const nodes = s();
        const hasRenderable = (vnodes) =>
          (vnodes || []).some((n) => {
            if (!n) return false;
            // Ignore comment nodes
            if (n.type === Comment) return false;
            // Text nodes: require non-whitespace
            if (n.type === Text) {
              return String(n.children ?? "").trim().length > 0;
            }
            // Fragments/templates or arrays: inspect children
            if (n.type === Fragment || Array.isArray(n.children)) {
              return hasRenderable(n.children);
            }
            // Element/component nodes count as content
            return true;
          });
        return hasRenderable(nodes);
      } catch (_) {
        return false;
      }
    },
    hasDropdownSlot() {
      const s = this.$slots["dropdown-menu"];
      if (!s) return false;
      try {
        const nodes = s();
        const hasRenderable = (vnodes) =>
          (vnodes || []).some((n) => {
            if (!n) return false;
            if (n.type === Comment) return false;
            if (n.type === Text) {
              return String(n.children ?? "").trim().length > 0;
            }
            if (n.type === Fragment || Array.isArray(n.children)) {
              return hasRenderable(n.children);
            }
            return true;
          });
        return hasRenderable(nodes);
      } catch (_) {
        return false;
      }
    },
    computedSrcset() {
      const index = this.item?.thumbnailIndex ?? 0;
      const perImageVariants = Array.isArray(this.item?.mediaVariants)
        ? this.item.mediaVariants[index]
        : null;
      const v =
        perImageVariants || this.item?.variants || this.item?.imageVariants;
      return this.buildVariantSrcset(v, 1024) || undefined;
    },
    cardImageSrc() {
      // A gateway-fallback override wins when a prior source failed to load.
      if (this.imageSrcOverride) return this.imageSrcOverride;
      const index = this.item?.thumbnailIndex ?? 0;
      const perImageVariants = Array.isArray(this.item?.mediaVariants)
        ? this.item.mediaVariants[index]
        : null;
      const variants =
        perImageVariants || this.item?.variants || this.item?.imageVariants;
      return this.pickCardVariant(variants, this.getDisplayImage());
    },
    cardImageCrossorigin() {
      // After a CORS attempt fails we retry without it, so drop the flag.
      if (this.imageCorsFallback) return undefined;
      const url = this.cardImageSrc || "";
      if (!url || /^data:/i.test(url)) return undefined;
      // Only request CORS mode from hosts that reliably send CORS headers so
      // canvas transparency sampling works; third-party NFT hosts often omit
      // them and would block the image entirely under crossorigin.
      if (/(?:firebasestorage\.googleapis\.com|storage\.googleapis\.com)/i.test(url)) {
        return "anonymous";
      }
      return undefined;
    },
    cardImageKey() {
      // Forces a fresh fetch when we switch from CORS to non-CORS mode.
      return `${this.cardImageSrc}::${this.imageCorsFallback ? "nocors" : "cors"}`;
    },
    computedSizes() {
      // Default heuristic for masonry grid: full width on small screens, ~33vw on larger
      return this.computedSrcset
        ? "(max-width: 590px) 50vw, 33vw"
        : undefined;
    },
    placeholderStyle() {
      // Apply dynamic max-height based on 9:16 (portrait) ratio of available width
      const styles = {
        minHeight: "200px",
        overflow: "hidden",
      };
      if (this.dynamicMaxHeightPx) {
        styles.maxHeight = this.dynamicMaxHeightPx + "px";
      }
      return styles;
    },
    displayTitle() {
      const raw = (this.item?.itemName || this.item?.title || "").toString();
      const t = raw.trim();
      if (!t) return "";
      // Filter out common placeholder copies we never want to render
      const placeholders = [
        "edit title",
        "give your post a title",
        "add a title",
        "post title",
      ];
      const lower = t.toLowerCase();
      if (placeholders.some((p) => lower === p || lower.startsWith(p))) {
        return "";
      }
      return t;
    },
  },
  methods: {
    pickCardVariant(variants, fallback) {
      if (!variants || typeof variants !== "object") return fallback;
      const dynamicCandidates = Object.entries(variants)
        .map(([key, url]) => {
          const match = /^w(\d+)$/.exec(key);
          return match ? { width: Number(match[1]), url } : null;
        })
        .filter((entry) => entry?.url && entry.width <= 1024)
        .sort((a, b) => b.width - a.width);
      return (
        variants.lg ||
        dynamicCandidates[0]?.url ||
        variants.md ||
        variants.sm ||
        fallback
      );
    },
    buildVariantSrcset(v, maxWidth = Infinity) {
      if (!v || typeof v !== "object") return "";
      const entries = [];
      [
        ["sm", 256],
        ["md", 512],
        ["lg", 1024],
        ["xl", 1920],
        ["xxl", 2560],
      ].forEach(([key, width]) => {
        if (v[key] && width <= maxWidth) entries.push(`${v[key]} ${width}w`);
      });
      // Accept any w### keys
      Object.keys(v)
        .filter((k) => /^w\d+$/.test(k) && Number(k.slice(1)) <= maxWidth)
        .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)))
        .forEach((k) => entries.push(`${v[k]} ${k.slice(1)}w`));
      return entries.join(", ");
    },
    /** Extract YouTube videoId from stored embedHtml iframe src (fallback for legacy posts without thumbnailUrl) */
    extractVideoIdFromEmbed() {
      try {
        const html = this.item?.embedHtml || "";
        const m = html.match(/youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})/i);
        return m ? m[1] : null;
      } catch { return null; }
    },
    _itemKey() {
      return (
        this.item?.id ||
        this.item?._id ||
        this.item?.docId ||
        this.item?.uid ||
        this.item?.slug ||
        "[no-id]"
      );
    },
    _log(...args) {
      try {
        // eslint-disable-next-line no-console
        // console.log("[ImageCard]", this._itemKey(), ...args);
      } catch (_) {}
    },
    getDisplayImage() {
      if (!this.item) return "";

      // Check images array first (new format)
      if (
        this.item.images &&
        Array.isArray(this.item.images) &&
        this.item.images.length > 0
      ) {
        const thumbnailIndex = this.item.thumbnailIndex || 0;
        if (this.item.images[thumbnailIndex]) {
          return this.item.images[thumbnailIndex];
        }
        return this.item.images[0]; // Fallback to first image
      }

      // Check mediaUrl (old format)
      if (this.item.mediaUrl) {
        return this.item.mediaUrl;
      }

      // Other possible field names
      return this.item.image || "";
    },
    handleImageLoaded(e) {
      const img = e.target;
      if (!img || !img.naturalWidth || !img.naturalHeight) return;
      this.imageLoadFailed = false;
      const ratio = img.naturalWidth / img.naturalHeight;
      // Map aspect ratio to caption length expansion
      // Base 70 (as before) around ratio ~1
      let extra = 0;
      if (ratio > 1.1 && ratio <= 1.6) extra = 20; // moderate landscape
      else if (ratio > 1.6 && ratio <= 2.2) extra = 40; // wide
      else if (ratio > 2.2) extra = 70; // very wide
      else if (ratio < 0.85) extra = -20; // tall image reduces caption
      const base = 70;
      this.captionMaxChars = Math.max(30, base + extra);

      // Detect actual transparency by sampling corner pixels on a tiny canvas
      if (!this.isAlphaMedia) {
        try {
          const c = document.createElement("canvas");
          c.width = 2;
          c.height = 2;
          const ctx = c.getContext("2d", { willReadFrequently: true });
          // Draw image scaled down to 2x2 — corners become the 4 pixels
          ctx.drawImage(img, 0, 0, 2, 2);
          const d = ctx.getImageData(0, 0, 2, 2).data;
          // Check alpha of each corner pixel (every 4th byte starting at index 3)
          for (let i = 3; i < d.length; i += 4) {
            if (d[i] < 250) {
              this.detectedTransparent = true;
              break;
            }
          }
        } catch (_) {
          // Cross-origin images can't be sampled — leave as non-transparent
        }
      }
    },
    handleImageError() {
      // 1) A forced CORS request can fail on third-party hosts that omit CORS
      //    headers (common for NFTs); retry once without crossorigin.
      if (this.cardImageCrossorigin === "anonymous" && !this.imageCorsFallback) {
        this.imageCorsFallback = true;
        return;
      }
      // 2) For IPFS-hosted images, retry through alternate gateways before
      //    giving up (e.g. ipfs.io returning 403/rate-limit).
      const current = this.imageSrcOverride || this.cardImageSrc || "";
      const cid = extractCID(current);
      if (cid) {
        while (this.imageGatewayIndex < IPFS_FALLBACK_GATEWAYS.length) {
          const candidate = `${IPFS_FALLBACK_GATEWAYS[this.imageGatewayIndex++]}${cid}`;
          if (candidate !== current) {
            this.imageCorsFallback = false;
            this.imageSrcOverride = candidate;
            return;
          }
        }
      }
      // 3) Signed authority download URLs expire after a few minutes; sign
      //    a fresh one once before giving up.
      if (!this.imageResignAttempted && assetIdForAuthorityUrl(current)) {
        this.imageResignAttempted = true;
        itemMediaUrlResolver.refreshUrl(current).then((fresh) => {
          if (this.cardImageSrc !== current) return;
          if (fresh && fresh !== current) this.imageSrcOverride = fresh;
          else this.imageLoadFailed = true;
        });
        return;
      }
      this.imageLoadFailed = true;
    },
    gridImageSrc(media, idx) {
      const override = this.gridSrcOverrides[idx];
      return override?.fresh || media.poster || media.thumbnailUrl || media.url;
    },
    handleGridImageError(media, idx) {
      const current = this.gridImageSrc(media, idx);
      if (this.gridSrcOverrides[idx] || !assetIdForAuthorityUrl(current)) return;
      this.gridSrcOverrides = { ...this.gridSrcOverrides, [idx]: { fresh: null } };
      itemMediaUrlResolver.refreshUrl(current).then((fresh) => {
        if (!fresh || fresh === current || !this.gridSrcOverrides[idx]) return;
        this.gridSrcOverrides = { ...this.gridSrcOverrides, [idx]: { fresh } };
      });
    },
    _setupResizeObserver() {
      const el = this.$refs.placeholderRef;
      if (!el) return;

      const compute = (width) => {
        const w = width || el.clientWidth || el.offsetWidth || 0;
        // Max-height capped at portrait 9:16 => height = width * (16/9)
        const maxH = Math.max(200, Math.round((w * 16) / 9));
        // Guard: only update if value actually changes
        if (this._lastMaxH !== maxH) {
          this._lastMaxH = maxH;
          // Defer update to next frame to avoid RO synchronous loop
          if (!this._rafPending) {
            this._rafPending = true;
            requestAnimationFrame(() => {
              this.dynamicMaxHeightPx = maxH;
              this._rafPending = false;
            });
          }
        }
      };

      // Initial compute
      compute();

      if (typeof ResizeObserver !== "undefined") {
        this._resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const width = entry?.contentRect?.width;
            compute(width);
          }
        });
        this._resizeObserver.observe(el);
      } else {
        // Fallback: window resize listener
        const onResize = () => compute();
        window.addEventListener("resize", onResize);
        this._resizeObserver = {
          disconnect: () => window.removeEventListener("resize", onResize),
        };
      }
    },
    _teardownResizeObserver() {
      if (
        this._resizeObserver &&
        typeof this._resizeObserver.disconnect === "function"
      ) {
        try {
          this._resizeObserver.disconnect();
        } catch (_) {}
      }
      this._resizeObserver = null;
    },
    onHoverStart() {
      this._log("mouseenter");
      this.startPreview();
    },
    onHoverEnd() {
      this._log("mouseleave");
      this.stopPreview();
    },
    onTouchStart() {
      this._touchMoved = false;
      clearTimeout(this._longPressTimer);
      this._log("touchstart");
      this._longPressTimer = setTimeout(() => {
        this._log("long-press trigger");
        this.startPreview();
      }, 350);
    },
    onPointerEnter(e) {
      this._log("pointerenter", { type: e?.pointerType });
    },
    onPointerLeave(e) {
      this._log("pointerleave", { type: e?.pointerType });
    },
    onMouseOver() {
      this._log("mouseover");
    },
    onMouseOut() {
      this._log("mouseout");
    },
    onTouchMove() {
      this._touchMoved = true;
      clearTimeout(this._longPressTimer);
      this._log("touchmove -> cancel long-press");
    },
    onTouchEnd() {
      clearTimeout(this._longPressTimer);
      // If we started playing due to long press, stop when touch ends
      this._log("touchend");
      if (this.isPreviewPlaying) this.stopPreview();
    },
    _attachVideoDebug(v) {
      if (!v || this._videoDebugAttached) return;
      const ev = (name) => () =>
        this._log(
          "video event:",
          name,
          "t=",
          v.currentTime.toFixed(2),
          "rs=",
          v.readyState,
        );
      v.addEventListener("loadedmetadata", ev("loadedmetadata"));
      v.addEventListener("loadeddata", ev("loadeddata"));
      v.addEventListener("canplay", ev("canplay"));
      v.addEventListener("canplaythrough", ev("canplaythrough"));
      v.addEventListener("play", ev("play"));
      v.addEventListener("pause", ev("pause"));
      v.addEventListener("timeupdate", ev("timeupdate"));
      v.addEventListener("error", () =>
        this._log("video event: error", v?.error),
      );
      this._videoDebugAttached = true;
    },
    async startPreview() {
      try {
        if (!this.isVideoItem) {
          this._log("startPreview() skipped: not a video item");
          return;
        }
        if (!this.videoSrc) {
          // Attempt an on-demand fetch from original doc to resolve a video URL
          const orig = this.item?._originalPath || this.item?.originalPath;
          if (orig && !this._fetchingVideo) {
            this._fetchingVideo = true;
            this._log(
              "startPreview(): resolving missing video via originalPath",
              orig,
            );
            try {
              const [collection, id] = orig.split("/");
              if (collection && id) {
                const snap = await getDoc(doc(db, collection, id));
                if (snap.exists()) {
                  const data = snap.data() || {};
                  const imgs = Array.isArray(data.images) ? data.images : [];
                  const tIdx =
                    this.item?.thumbnailIndex ?? data.thumbnailIndex ?? 0;
                  const poster = imgs[tIdx] ?? imgs[0] ?? "";
                  const alts = [
                    data.mediaUrl,
                    data.videoUrl,
                    data.processedVideoUrl,
                    data.originalVideoUrl,
                    ...imgs.filter((u, i) => i !== tIdx && u && u !== poster),
                  ].filter((u) => typeof u === "string" && u);
                  const candidate = alts[0] || "";
                  if (candidate) {
                    this.resolvedVideoSrc = candidate;
                    this._log(
                      "Resolved videoSrc from original doc:",
                      candidate,
                    );
                  }
                }
              }
            } catch (err) {
              this._log(
                "Failed to resolve video from original doc:",
                err?.message || err,
              );
            } finally {
              this._fetchingVideo = false;
            }
          }
          if (!this.videoSrc) {
            this._log("startPreview() skipped: missing videoSrc");
            return;
          }
        }
        this.videoLoadRequested = true;
        await this.$nextTick();
        const v = this.$refs.videoRef;
        if (!v) {
          this._log("startPreview() skipped: no <video> element found");
          return;
        }
        this._attachVideoDebug(v);
        this._log("startPreview()", {
          videoSrc: this.videoSrc,
          posterUrl: this.posterUrl,
          displayUrl: this.displayUrl,
          hasImageMedia: this.hasImageMedia,
          displayIsVideo: this.displayIsVideo,
          readyState: v.readyState,
        });
        v.muted = true;
        v.playsInline = true;
        // Load metadata once but don't hang indefinitely
        if (v.readyState < 2) {
          try {
            const onReady = new Promise((resolve) => {
              const handler = () => resolve();
              v.addEventListener("canplay", handler, { once: true });
              v.addEventListener("loadeddata", handler, { once: true });
            });
            try {
              v.load();
            } catch (_) {}
            await Promise.race([
              onReady,
              new Promise((r) => setTimeout(r, 600)),
            ]);
          } catch {}
        }
        // Optimistically hide overlay immediately; revert if play fails
        this.isPreviewPlaying = true;
        this._log("isPreviewPlaying = true (pre-play)");
        try {
          await v.play();
          this._log("video.play() resolved");
        } catch (err) {
          this._log(
            "play() rejected",
            (err && (err.name || err.message)) || err,
          );
          this.isPreviewPlaying = false;
          this._log("isPreviewPlaying = false (due to play() error)");
        }
      } catch (e) {
        // autoplay may be blocked; keep overlay visible
        this._log("Preview play failed", (e && (e.name || e.message)) || e);
      }
    },
    stopPreview() {
      const v = this.$refs.videoRef;
      if (!v) {
        this.isPreviewPlaying = false;
        this._log("isPreviewPlaying = false; no video ref on stop");
        return;
      }
      try {
        v.pause();
        // Optionally reset time for consistent preview
        v.currentTime = 0;
      } catch {}
      this.isPreviewPlaying = false;
      this._log("isPreviewPlaying = false; paused and reset to 0");
    },
    // Audio playback methods
    toggleAudioPlayback() {
      const audio = this.$refs.audioElement;
      if (!audio) return;
      if (this.isAudioPlaying) {
        audio.pause();
      } else {
        audio.play().catch((err) => {
          console.warn("[ImageCard] Audio play failed:", err);
        });
      }
    },
    onAudioPlay() {
      this.isAudioPlaying = true;
    },
    onAudioPause() {
      this.isAudioPlaying = false;
    },
    onAudioEnded() {
      this.isAudioPlaying = false;
    },
  },
  data() {
    return {
      // Base default; will adjust on image load or for text-only media
      captionMaxChars: 70,
      dynamicMaxHeightPx: 0,
      _resizeObserver: null,
      _lastMaxH: 0,
      _rafPending: false,
      isPreviewPlaying: false,
      _longPressTimer: null,
      _touchMoved: false,
      _videoDebugAttached: false,
      resolvedVideoSrc: "",
      videoLoadRequested: false,
      _fetchingVideo: false,
      isAudioPlaying: false,
      imageLoadFailed: false,
      imageCorsFallback: false,
      imageSrcOverride: null,
      imageGatewayIndex: 0,
      imageResignAttempted: false,
      gridSrcOverrides: {},
      detectedTransparent: false,
    };
  },
  created() {
    // For text-only media decide limits without image
    if (this.isTextOnlyMedia) {
      if (this.item.mediaType === "poster")
        this.captionMaxChars = 0; // poster has no caption by definition
      else if (this.item.mediaType === "tweet") this.captionMaxChars = 140;
      else if (this.item.mediaType === "post") this.captionMaxChars = 280;
    }
  },
  watch: {
    isTextOnlyMedia: {
      immediate: true,
      handler(val) {
        if (val) {
          if (this.item.mediaType === "poster") this.captionMaxChars = 0;
          else if (this.item.mediaType === "tweet") this.captionMaxChars = 140;
          else if (this.item.mediaType === "post") this.captionMaxChars = 280;
        }
      },
    },
    showVisualPlaceholder: {
      immediate: true,
      handler(isPlaceholder) {
        this.$nextTick(() => {
          if (isPlaceholder) this._setupResizeObserver();
          else this._teardownResizeObserver();
        });
      },
    },
    isPreviewPlaying(val) {
      this._log("watch isPreviewPlaying ->", val);
    },
    displayUrl() {
      // Reset per-image load state when the underlying source changes (reuse).
      this.imageLoadFailed = false;
      this.imageCorsFallback = false;
      this.imageSrcOverride = null;
      this.imageGatewayIndex = 0;
      this.imageResignAttempted = false;
      this.gridSrcOverrides = {};
      this.detectedTransparent = false;
    },
  },
  mounted() {
    if (this.showVisualPlaceholder) this._setupResizeObserver();
    this._log("mounted with", {
      isVideoItem: this.isVideoItem,
      videoSrc: this.videoSrc,
      posterUrl: this.posterUrl,
      displayUrl: this.displayUrl,
      hasImageMedia: this.hasImageMedia,
      displayIsVideo: this.displayIsVideo,
    });
    // Also attach native pointer events for verification
    const el = this.$refs.containerRef;
    if (el && el.addEventListener) {
      el.addEventListener("mouseenter", () => this._log("[native] mouseenter"));
      el.addEventListener("mouseleave", () => this._log("[native] mouseleave"));
      el.addEventListener("pointerenter", (e) =>
        this._log("[native] pointerenter", { type: e?.pointerType }),
      );
      el.addEventListener("pointerleave", (e) =>
        this._log("[native] pointerleave", { type: e?.pointerType }),
      );
      el.addEventListener("mouseover", () => this._log("[native] mouseover"));
      el.addEventListener("mouseout", () => this._log("[native] mouseout"));
    }
  },
  beforeUnmount() {
    this._teardownResizeObserver();
  },
};
</script>

<style scoped>
.image-card {
  cursor: pointer;
  position: relative; /* Needed for absolute positioning of buttons */
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  overflow: visible; /* Allow buttons to be visible outside the container */
}

.image-card__img-container {
  max-height: 60vh;
  background-color: var(--color-surface-subtle);
  overflow-y: auto; /* Only the image container scrolls */
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  position: relative;
}

/* Alpha-capable formats: remove card background, add padding */
.image-card__img-container.transparent-media {
  background-color: transparent;
  padding: var(--space-5);
}

/* Wider top corners for audio cards without cover image */
.image-card__img-container:has(> .audio-container:not(.has-cover)) {
  border-radius: var(--radius-pill) var(--radius-pill) 0 0;
}

.image-card__img {
  width: 100%;
  height: auto;
  display: block;
  background: var(--color-surface-raised);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  position: relative;
  z-index: 1;
}

.image-card__img.transparent-media {
  background: transparent;
}

/* ===== MULTI-IMAGE GRID ===== */
.multi-image-grid {
  display: grid;
  gap: 2px;
  background: var(--color-surface-raised);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  overflow: hidden;
  aspect-ratio: 1 / 1; /* Square container */
}

/* 2 images: stacked vertically */
.multi-image-grid.grid-2 {
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 1fr;
}

/* 3 images: 1 tall left + 2 stacked right */
.multi-image-grid.grid-3 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}
.multi-image-grid.grid-3 .cell-0 {
  grid-row: 1 / 3;
}

/* 4 images: 2x2 grid */
.multi-image-grid.grid-4 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.grid-cell {
  position: relative;
  overflow: hidden;
}

.grid-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.grid-cell-video {
  background: #000;
}

.grid-video-overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
}

.grid-play-icon {
  width: 38px;
  height: 38px;
  color: #fff;
  border-radius: 999px;
  padding: 9px;
  background: rgba(0, 0, 0, 0.45);
}

/* +N overlay for 5+ images */
.grid-more-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  color: #fff;
}

.grid-more-icon {
  width: 24px;
  height: 24px;
  stroke: #fff;
  opacity: 0.9;
}

.grid-more-count {
  font-size: var(--font-size-body);
  font-weight: 600;
}

/* Rounded corners for grid */
.multi-image-grid.rounded-all {
  border-radius: var(--radius-sm);
}
.multi-image-grid.rounded-bottom {
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

/* ===== END MULTI-IMAGE GRID ===== */

.play-overlay {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 50%;
  z-index: 3;
  pointer-events: none; /* let click bubble to the card for selection */
}
.play-overlay .play-icon {
  width: 36px;
  height: 36px;
  color: #fff;
}

/* Minted label - discrete text indicator for on-chain NFTs */
.minted-label {
  font-size: var(--font-size-label);
  color: var(--color-text-subtle);
  margin-right: var(--space-2);
  flex-shrink: 0;
}

/* Batch select checkbox for multi-image posts */
.batch-select-checkbox {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  margin-right: var(--space-4);
  flex-shrink: 0;
}
.batch-select-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--color-control-success-accent);
}
.batch-select-checkbox .checkbox-label {
  font-size: var(--font-size-label);
  color: var(--color-feedback-success-foreground);
  font-weight: 500;
}

/* Video thumbnail placeholder when the media item is a video file */
.video-thumb {
  position: relative;
  width: 100%;
  min-height: 200px;
  background: var(--color-surface-raised);
  display: block;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  overflow: hidden;
}
.video-thumb__bg {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  min-height: 200px;
}
/* Video element sits absolutely on top of the poster thumbnail.
   Hidden by default; shown only while the preview is playing. */
.image-card__video {
  width: 100%;
  height: 100%;
  display: block;
  background: var(--color-surface-raised);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}
/* When no poster image exists, make the video visible so its first frame provides layout sizing */
.image-card__video.no-poster {
  position: relative;
  opacity: 1;
}
.image-card__video.is-playing {
  opacity: 1;
  pointer-events: auto;
}

/* Corner handling: we only apply full rounding when there's no info block; when info exists, bottom corners are supplied by the info container. */

.image-card__info {
  padding: var(--space-2) 0;
  background: transparent;
  color: var(--color-text-primary);
  overflow: visible;
  bottom: 0;
  left: 0;
  width: 100%;
  max-height: 300px;
  border-radius: 0 0 var(--radius-sm) var(--radius-sm);
  display: flex;
  flex-direction: column;
}

/* Remove padding and background when no caption */
.image-card__info.no-caption {
  padding: var(--space-2) 0;

  background: transparent;
  border-radius: 0;
}

/* Caption row: caption text and dropdown on same line */
.caption-row {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  width: 100%;
  justify-content: flex-end; /* Align dropdown to right when no caption */
}

.caption-text {
  padding-top: 1px;
  flex: 1;
  min-width: 0; /* Allow text to shrink */
  margin-right: auto; /* Push dropdown to right when caption exists */
}

.read-more-link {
  color: var(
    --color-border-subtle
  ); /* Subtle gray, closer to background in both themes */
  cursor: pointer;
  text-underline-offset: 2px; /* Slight offset for better readability */
  font-weight: 500;
  margin-left: var(--space-2);
}

.read-more-link:hover {
  opacity: 0.8;
}

/* Audio text clickable - for audio items without cover image */
.audio-text-clickable {
  cursor: pointer;
  transition: color 0.15s ease;
}

.audio-text-clickable:hover {
  color: var(--color-text-tertiary);
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* If product/service with caption, treat image top-only radius and give info its own bottom radius (already) - ensure no double radius gap */
.image-card__img:not(.rounded-all) {
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.image-card__info-footer {
  margin-top: auto;
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  gap: var(--space-1);
}

.saving-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  pointer-events: none;
}
.saving-shimmer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.6),
    transparent
  );
  animation: shimmer 1.2s infinite;
}
.saving-text {
  background: rgba(255, 255, 255, 0.8);
  color: var(--color-text-primary);
  border-radius: 10px;
  margin: var(--space-3);
  padding: 2px 8px;
}
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Dropdown menu container in caption row */
.dropdown-menu-container {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.rounded-all {
  border-radius: var(--radius-sm) !important;
}

.rounded-bottom {
  border-bottom-left-radius: var(--radius-sm) !important;
  border-bottom-right-radius: var(--radius-sm) !important;
}

/* NFT Placeholder Styles */
.nft-placeholder-container {
  width: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  position: relative;
  z-index: 1;
}

.nft-placeholder-icon {
  /* override: ensure transparent bg so gradient shows */
  background: transparent;
  padding: var(--space-1);
}

.nft-placeholder-icon .placeholder-svg {
  width: 48px;
  height: 48px;
  stroke: var(--color-surface-canvas);
  opacity: 0.9;
}

@media (max-width: 590px) {
  .nft-placeholder-icon .placeholder-svg {
    width: 40px;
    height: 40px;
  }
}

.nft-placeholder-text {
  font-size: 0.9rem;
  font-weight: bold;
  color: white;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  max-width: 80%;
  word-wrap: break-word;
}

/* Ensure NFT captions are left-aligned like other items */
.image-card__info {
  text-align: left;
}

/* Color variants for NFT placeholders */
.nft-placeholder-0 {
  background: linear-gradient(135deg, #ff6b6b, #ff8e8e);
}
.nft-placeholder-1 {
  background: linear-gradient(135deg, #4ecdc4, #6eddd6);
}
.nft-placeholder-2 {
  background: linear-gradient(135deg, #45b7d1, #67c3dd);
}
.nft-placeholder-3 {
  background: linear-gradient(135deg, #96ceb4, #a8d5c4);
}
.nft-placeholder-4 {
  background: linear-gradient(135deg, #ffeaa7, #ffefb7);
}
.nft-placeholder-5 {
  background: linear-gradient(135deg, #dda0dd, #e6b3e6);
}
.nft-placeholder-6 {
  background: linear-gradient(135deg, #98d8c8, #a8ddd3);
}
.nft-placeholder-7 {
  background: linear-gradient(135deg, #f7dc6f, #f9e285);
}
.nft-placeholder-8 {
  background: linear-gradient(135deg, #bb8fce, #c7a2d6);
}
.nft-placeholder-9 {
  background: linear-gradient(135deg, #85c1e9, #97caf0);
}

/* Adjust text color for lighter backgrounds */
.nft-placeholder-4 .nft-placeholder-text,
.nft-placeholder-7 .nft-placeholder-text {
  color: #333;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
}

/* Document display styles */
.document-container {
  width: 100%;
  background: var(--color-surface-raised);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  position: relative;
  z-index: 1;
  padding: var(--space-4);
}

/* √2 (A-paper) aspect frame: width:height = 1:1.414... */
.document-frame {
  position: relative;
  width: 100%;
  /* Use native aspect-ratio when available */
  aspect-ratio: 1 / 1.414;
  /* Fallback for browsers without aspect-ratio */
  --_fallback-aspect: calc(100% * 1.414);
  overflow: hidden;
}
.document-frame::before {
  content: "";
  display: block;
  height: 0;
  padding-top: 141.4%; /* 1.414 * 100% */
}
@supports (aspect-ratio: 1 / 1.414) {
  .document-frame::before {
    content: none;
    padding-top: 0;
  }
}
.document-frame > * {
  position: absolute;
  inset: 0;
}

/* PDF embed fills the frame; allow scroll inside if taller content appears */
.document-embed {
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

/* Fallback card centered within the frame */
.document-fallback-card {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.02));
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.document-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 50%;
  padding: var(--space-4);
}

.document-icon .doc-svg {
  width: 32px;
  height: 32px;
  fill: var(--color-icon-supporting);
}

.document-name {
  font-size: var(--font-size-body);
  color: var(--color-text-primary);
  text-align: center;
  max-width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

/* Document type badge */
.document-type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: var(--font-size-caption, 11px);
  font-weight: 600;
  background: var(--color-surface-raised);
  color: var(--color-text-emphasis);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

/* Text document preview in masonry card */
.document-text-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  overflow: hidden;
}
.document-text-header {
  display: flex;
  align-items: center;
  gap: var(--space-3, 6px);
  padding: var(--space-3, 6px) var(--space-4, 8px);
  border-bottom: 1px solid var(--color-surface-raised);
  flex-shrink: 0;
}
.document-text-name {
  font-size: var(--font-size-label, 12px);
  color: var(--color-text-supporting);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.document-text-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  padding: var(--space-4, 8px);
  margin: 0;
  font-family: inherit;
  font-size: var(--font-size-label, 12px);
  line-height: 1.5;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-wrap: break-word;
  tab-size: 4;
  /* Fade out at bottom to indicate scrollable content */
  -webkit-mask-image: linear-gradient(to bottom, black 80%, transparent 100%);
  mask-image: linear-gradient(to bottom, black 80%, transparent 100%);
}

/* Empty placeholder fallback */
.empty-placeholder {
  width: 100%;
  background-color: var(--color-surface-raised);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
}

.empty-placeholder-icon {
  font-size: 3rem;
  color: var(--color-icon-placeholder);
  opacity: 0.6;
}

.empty-placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: var(--space-4);
  gap: var(--space-4);
  min-height: 0; /* allow child to flex-scroll */
}

.empty-placeholder-title {
  font-family: var(--font-family-ui);
  font-size: var(--font-size-display);
  line-height: 1.05;
}

.empty-placeholder-caption {
  margin-top: var(--space-4);
  opacity: 0.9;
  font-size: var(--font-size-body);
  overflow: auto;
  width: 100%;
  flex: 1; /* take remaining space and scroll if needed */
  min-height: 0;
}

/* ========== EMBED CONTAINER (YouTube, SoundCloud) — Thumbnail + Play ========== */
.embed-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background-color: #000;
  cursor: pointer;
}

.embed-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.embed-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-surface-raised);
}
.embed-placeholder .small {
  width: 32px;
  height: 32px;
  stroke: var(--color-text-secondary);
}

/* Semi-transparent play button overlay */
.embed-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  transition: background 0.2s ease;
}
.embed-container:hover .embed-play-overlay {
  background: rgba(0, 0, 0, 0.4);
}
.embed-play-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;
}
.embed-container:hover .embed-play-btn {
  transform: scale(1.1);
}
.play-icon-svg {
  width: 24px;
  height: 24px;
  transform: translateX(5%);
}

.embed-container.embed-vertical {
  aspect-ratio: 9 / 16;
}

.embed-container.embed-square {
  aspect-ratio: 1 / 1;
}

.embed-container.embed-square .embed-thumbnail {
  transform: scale(1.35);
}

.embed-container.rounded-all {
  border-radius: var(--radius-sm);
}

.embed-container.rounded-bottom {
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.embed-link-card {
  width: 100%;
  min-height: 210px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  background:
    radial-gradient(circle at top left, rgba(29, 185, 84, 0.22), transparent 38%),
    linear-gradient(135deg, rgba(18, 18, 18, 0.98), rgba(32, 32, 32, 0.94));
  border: 1px solid rgba(29, 185, 84, 0.24);
}

.embed-link-card.rounded-all {
  border-radius: var(--radius-sm);
}

.embed-link-card.rounded-bottom {
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.embed-link-card__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.68);
}

.embed-link-card__title {
  color: #fff;
  font-size: 1rem;
  line-height: 1.25;
  font-weight: 600;
}

.embed-link-card__button {
  width: fit-content;
  padding: 9px 14px;
  border-radius: 999px;
  background: #1db954;
  color: #04130a;
  font-weight: 700;
  text-decoration: none;
}

.embed-link-card__button:hover {
  filter: brightness(1.05);
}

/* SoundCloud / other embed — live inline widget */
.embed-widget-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: var(--color-surface-raised);
}
.embed-widget-container iframe {
  width: 100%;
  border: 0;
  display: block;
}
.embed-widget-container.rounded-all {
  border-radius: var(--radius-sm);
}
.embed-widget-container.rounded-bottom {
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

/* Wrapper for embed widget + expand footer */
.embed-widget-wrapper {
  position: relative;
  width: 100%;
}

/* Expand footer for SoundCloud / non-YouTube embeds — overlaid at bottom */
.embed-expand-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: var(--space-2) var(--space-4);
  cursor: pointer;
  background-color: var(--color-surface-raised);
  transition: background-color 0.15s ease;
  z-index: 2;
}
.embed-expand-footer:hover {
  background-color: var(--color-surface-subtle);
}
.embed-expand-footer.rounded-all-bottom {
  border-radius: 0 0 var(--radius-sm) var(--radius-sm);
}

/* Spotify expand footer — flush below the embed (like audio), not overlaid */
.spotify-embed-wrapper .embed-expand-footer {
  position: relative;
  bottom: auto;
}
.embed-expand-label {
  font-size: var(--font-size-label);
  color: var(--color-text-primary);
}

/* ========== AUDIO CARD STYLES ========== */
.audio-container {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* Isolate stacking context to prevent color blending issues */
  isolation: isolate;
}

/* When no cover image, use static positioning for the audio wrapper */
.audio-container:not(.has-cover) {
  min-height: auto;
  justify-content: flex-start;
  padding: 0;
}

.audio-container:not(.has-cover) .audio-native-wrapper {
  position: relative;
  background: transparent;
  z-index: 2;
}

.audio-container.rounded-all {
  border-radius: var(--radius-sm);
}

.audio-container.rounded-bottom {
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

/* Background element behind audio player (no cover) — fills space below halfway point */
.audio-player-bg {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(20px + var(--space-2) + var(--space-4)); /* half player + expand footer height */
  background: var(--color-surface-raised);
  z-index: 1;
}

/* Expand footer for audio (no cover) — same style as embed-expand-footer */
.audio-expand-footer {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: var(--space-2) var(--space-4);
  cursor: pointer;
  background-color: var(--color-surface-raised);
  transition: background-color 0.15s ease;
  z-index: 3;
}

.audio-expand-footer:hover {
  background-color: var(--color-surface-subtle);
}

.audio-expand-footer.rounded-all-bottom {
  border-radius: 0 0 var(--radius-sm) var(--radius-sm);
}

.audio-expand-label {
  font-size: var(--font-size-label);
  color: var(--color-text-primary);
}

.audio-container.has-cover {
  min-height: auto;
}

.audio-cover-image {
  position: relative;
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  z-index: 0;
}

.audio-cover-image.audio-cover-clickable {
  cursor: pointer;
  transition: filter 0.15s ease;
}

.audio-cover-image.audio-cover-clickable:hover {
  filter: brightness(1.05);
}

.audio-card-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-5);
  gap: var(--space-4);
  flex: 1;
}

.audio-card-content.with-cover {
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0.2) 50%,
    transparent 100%
  );
  justify-content: flex-end;
  padding-bottom: var(--space-7);
}

/* Play/Pause button */
.audio-native-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: var(--space-4);
  background: var(--color-surface-canvas);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
}

.audio-native-wrapper.with-cover {
  position: relative;
  background: var(--color-surface-canvas);
  padding-left: 0;
  padding-right: 0;
}

.native-audio-player {
  width: 100%;
  height: 40px;
}

/* Expand button */
.audio-expand-button {
  position: absolute;
  top: var(--space-4);
  right: var(--space-4);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-surface-canvas);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.15s ease, transform 0.15s ease;
  z-index: 3;
}

.audio-expand-button:hover {
  opacity: 1;
  transform: scale(1.1);
}

.audio-container.has-cover .audio-expand-button {
  background: rgba(255, 255, 255, 0.25);
}

.audio-expand-icon {
  width: 18px;
  height: 18px;
  stroke: var(--color-text-primary);
}

.audio-container.has-cover .audio-expand-icon {
  stroke: #fff;
}
</style>
